import React, { useState,  useEffect, Fragment } from 'react';
import { FormProvider } from 'react-hook-form';

/* Icons */
import CheckIcon from '~/../symbols/check.svg';

/* Components */
import ConversionFlowComponentRenderer from './ConversionFlowComponentRenderer';

/* Common */
import { Button } from '~/components/common';
import { Icon } from '~/components/common';

/* Utilities */
import { trans } from '~/utilities';

/* Helpers */
import { pushDatalayer } from '../../../utilities/datalayer';

/* Custom */
import { activatePrefillScript } from '~/custom/prefillFormFields';

export default function ConversionFlowForm({ form, flow, doRequest, flowStep, status }) {
    const [flowGroups, setFlowGroups] = useState(undefined);
    const [activeStep, setActiveStep] = useState(0);
    const [prevActiveStep, setPrevActiveStep] = useState(null);
    const [finishedGroups, setFinishedGroups] = useState({});
    const gclId = sessionStorage.getItem('gclid') ? sessionStorage.getItem('gclid') : '';
    const utmSource = sessionStorage.getItem('utm_source') ? sessionStorage.getItem('utm_source') : '';
    const utmMedium = sessionStorage.getItem('utm_medium') ? sessionStorage.getItem('utm_medium') : '';
    const monthlyPrice = sessionStorage.getItem('monthly') ? sessionStorage.getItem('monthly') : '';
    const totalPrice = sessionStorage.getItem('price') ? sessionStorage.getItem('price') : '';
    const subscription = sessionStorage.getItem('subscription') ? sessionStorage.getItem('subscription') : '';
    const delivery = sessionStorage.getItem('delivery') ? sessionStorage.getItem('delivery') : '';

    if (flow && gclId !== '') {
        flow.gclid = gclId;
    }

    if (flow && utmSource !== '') {
        flow.utmSource = utmSource;
    }

    if (flow && utmMedium !== '') {
        flow.utmMedium = utmMedium;
    }

    if (flow && monthlyPrice !== '') {
        flow.monthlyPrice = monthlyPrice;
    }

    if (flow && totalPrice !== '') {
        flow.totalPrice = totalPrice;
    }

    if (flow && subscription !== '') {
        flow.subscription = JSON.parse(subscription);
    }

    if (flow && delivery !== '') {
        flow.delivery = JSON.parse(delivery);
    }

    useEffect(() => {
        if (flow && flow.steps) {
            const activeStep = flow.steps.find(step => step.active);
            if (activeStep && (!prevActiveStep || activeStep.position > prevActiveStep.position)) {
                const forms = Array.from(document.querySelectorAll('form'));
                activatePrefillScript(forms);
            }
            setPrevActiveStep(activeStep);
        }
    }, [flow?.steps]);

    useEffect(() => {
        if (flow && flow.form) {
            setFlowGroups(getGroups());
        }
    }, [flow]);

    useEffect(() => {
        setActiveStep(0);
        updateGroups(0);
    }, [flowStep.position]);

    const getGroups = () => {
        const groups = flow.form.components.reduce(function(value, item) {
            (value[item['group']] = value[item['group']] || []).push(item);
            (value[item['group']] = value[item['group']] || []).step = flowStep.position;
            return value;
        }, {});

        return groups;
    }

    const updateGroups = (groupStep) => {
        const newFlowGroups = getGroups();

        Object.entries(finishedGroups || {}).map(([key, value], index) => {
            value.completed = value.step !== flowStep.position ? true : index < groupStep;
        });

        Object.entries(newFlowGroups || {}).map(([key, value], index) => {
            value.completed = value.step !== flowStep.position ? true : index < groupStep;
        });

        setFinishedGroups({
            ...finishedGroups,
            ...newFlowGroups
        });
    }

    const sendDatalayer = (flow) => {
        let stepName = '';
        const activeStep = flow?.steps?.find(step => step.id === flow?.steps?.find(step => step.active)?.id) || null;
        let datalayerObject = {};

        if (activeStep.position > 0) {
            stepName = 'Abonnementen';
            datalayerObject['inruilvoorstel'] = flow?.datalayer?.enriched?.inruilvoorstel ? flow.datalayer.enriched.inruilvoorstel : 'null';
            datalayerObject['zekerheid'] = flow?.datalayer?.enriched?.zekerheid ? flow.datalayer.enriched.zekerheid : 'null';
            datalayerObject['onderhoudspakket'] = flow?.datalayer?.enriched?.onderhoudspakket ? flow.datalayer.enriched.onderhoudspakket : 'null';
            datalayerObject['zekerheidspakket'] = flow?.datalayer?.enriched?.zekerheidspakket ? flow.datalayer.enriched.zekerheidspakket : 'null';
            datalayerObject['verzekeren'] = flow?.datalayer?.enriched?.verzekeren ? flow.datalayer.enriched.verzekeren : 'null';
        }

        if (activeStep.position > 1) {
            stepName = 'Gegevens';
            datalayerObject['email'] = flow?.datalayer?.enriched?.email ? flow.datalayer.enriched.email : 'null';
            datalayerObject['phone_number'] = flow?.datalayer?.enriched?.phone_number ? flow.datalayer.enriched.phone_number : 'null';
            datalayerObject['tenaamstelling'] = flow?.datalayer?.enriched?.tenaamstelling ? flow.datalayer.enriched.tenaamstelling : 'null';
        }

        if (activeStep.position > 2) {
            stepName = 'Bestellen';
            datalayerObject['aflevering'] = flow?.datalayer?.enriched?.aflevering ? flow.datalayer.enriched.aflevering : 'null';
            datalayerObject['aflever_moment'] = flow?.datalayer?.enriched?.aflever_moment ? flow.datalayer.enriched.aflever_moment : 'null';
        }

        pushDatalayer({
            page_event: 'form_data',
            page_type: 'form',
            form_name: 'kopen',
            form_step: activeStep?.position,
            form_step_name: stepName,
            event: 'form',
            items: getVehicleData(),
            ...datalayerObject
        });
    }

    const getVehicleData = () => {
        return [{
            item_source:                    `${flow?.summary?.vehicle?.source === 'Occassion' ? 'gebruikt' : 'nieuw'}`,
            item_brand:                     `${flow?.summary?.vehicle?.brand ? flow.summary.vehicle.brand : 'null'}`,
            item_model:                     `${flow?.summary?.vehicle?.model ? flow.summary.vehicle.model : 'null'}`,
            item_price:                     `${flow?.summary?.vehicle?.price ? flow.summary.vehicle.price : 'null'}`,
            item_odemeter_distance:         `${flow?.summary?.vehicle?.tellerstand ? flow.summary.vehicle.tellerstand : 'null'}`,
            item_construction_year:         `${flow?.summary?.vehicle?.bouwjaar ? flow.summary.vehicle.bouwjaar : 'null'}`,
            item_fuel_type:                 `${flow?.summary?.vehicle?.brandstof ? flow.summary.vehicle.brandstof : 'null'}`,
            item_transmission:              `${flow?.summary?.vehicle?.transmissie ? flow.summary.vehicle.transmissie : 'null'}`,
            item_body:                      `${flow?.summary?.vehicle?.carrosserie ? flow.summary.vehicle.carrosserie : 'null'}`,
            item_color:                     `${flow?.summary?.vehicle?.kleur ? flow.summary.vehicle.kleur : 'null'}`,
            item_establishment:             `${flow?.summary?.vehicle?.vestiging ? flow.summary.vehicle.vestiging : 'null'}`,
            item_average_usage:             `${flow?.summary?.vehicle?.gemiddeld_verbruik ? flow.summary.vehicle.gemiddeld_verbruik: 'null'}`,
        }];
    }

    return (
        <form noValidate onSubmit={form.handleSubmit(() => {
            if (activeStep === (Object.entries(flowGroups || {}).length - 1)) {
                sendDatalayer(flow);
                doRequest({ action: 'submit' }, 'submitting');
            } else {
                setActiveStep(activeStep + 1);
                updateGroups(activeStep + 1);
                doRequest();
            }
        })}>
            <FormProvider {...form}>
                <div className="row">
                    <div className="col-12">
                        {flow?.form?.title && (
                            <p className="text--lg font-weight-bold mb-1">{flow.form.title}</p>
                        )}

                        <p className="text--md mb-0">{flow?.form?.intro}</p>

                        {(flowGroups) && (
                            <div className="accordion accordion--conversion" id="flowAccordion">
                                {Object.entries(flowGroups || {}).map(([key, value], index) => (
                                    <Fragment key={index}>
                                        <button className={`btn btn--none btn--accordion p-0 ${index === activeStep ? 'active' : ''}`} disabled={index > activeStep} type="button" data-toggle="collapse" data-target={`#${key}`} aria-expanded={index === activeStep} aria-controls={key} onClick={() => {
                                            setActiveStep(index);
                                            updateGroups(index);
                                        }}>
                                            <div className="w-100">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    {index < activeStep && (
                                                        <Icon
                                                            size={10}
                                                            className={`btn__icon icon icon--lg-18`}
                                                            url={CheckIcon?.url}
                                                        />
                                                    )}
                                                </div>
                                                {(index < activeStep && flow.summary.groups[key].value.length > 0) && (
                                                    <div className="flow__header-intro mt-2">
                                                        {flow.summary.groups[key].value.map((value, index) => (
                                                            <p key={index} className="w-100 mb-1">{value}</p>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                        <div id={key} className={`accordion__item collapse p-0 ${index === activeStep ? 'show' : ''}`} aria-labelledby="headingOne" data-parent="#flowAccordion">
                                            <div className="row gutters-4 gutters-lg-5">
                                                {index <= activeStep && (
                                                    <ConversionFlowComponentRenderer flow={flow} group={key} />
                                                )}
                                                <div className="col-12">
                                                    <Button
                                                        className="mt-lg-3 btn-block"
                                                        theme="success"
                                                        loading={status !== 'idle'}
                                                        disabled={status !== 'idle'}
                                                        loadingSize={14}
                                                        title={index === (Object.entries(flowGroups || {}).length - 1) ? flow?.form?.buttonLabel : trans('app.flow.next_step_button')}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Fragment>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </FormProvider>
        </form>
    )
}
